/**
 * The evidence ledger and the doubt a changed record propagates.
 *
 * A source check is a dated observation of a search, never a truth value. The
 * latest observation still lives at `node.verification`; earlier observations
 * live in `verificationHistory`. This module is the one place that joins them,
 * compares them, and answers which reasoning predates a material change.
 *
 * Nothing here asks a model or stores a verdict. A diff is set arithmetic over
 * records already admitted by the citation gate, and staleness is reachability
 * plus a monotonic insertion ordinal (with timestamps only for legacy rows). It
 * means only: this reasoning has not been rebuilt against the newer evidence
 * record.
 */

import { buildEvidenceGenealogy, type EvidenceRecord } from './genealogy'
import { corpusEvidenceRecordId, webEvidenceRecordId } from './evidenceIds'
import { canonicalUrl } from './url'
import { verificationLive } from './types'
import { writtenAt } from './rewrite'
import type {
  PriorArt,
  ReasonNode,
  Session,
  SourceReading,
  Verification,
  VerificationArchiveEntry,
  WebSource,
} from './types'

export interface DeltaEntry {
  key: string
  label: string
}

export interface EvidenceDelta {
  added: DeltaEntry[]
  removed: DeltaEntry[]
  changed: DeltaEntry[]
}

export interface VerificationDiff {
  beforeAt?: number
  afterAt: number
  readingBefore?: SourceReading
  readingAfter: SourceReading
  readingChanged: boolean
  claimChanged: boolean
  modelChanged: boolean
  scopeChanged: boolean
  partialChanged: boolean
  countsChanged: boolean
  coverageChanged: boolean
  summaryChanged: boolean
  sources: EvidenceDelta
  corpus: EvidenceDelta
  priorArt: EvidenceDelta
  queries: EvidenceDelta
  failures: EvidenceDelta
  notices: EvidenceDelta
  /** Any visible or provenance-bearing change, including a provider change. */
  hasChanges: boolean
  /** A change conservative enough to make dependent reasoning worth revisiting. */
  affectsReasoning: boolean
}

export interface EvidenceChange {
  /** Claim node on which the material re-check landed. */
  sourceId: string
  /** Exact evidence records whose admitted representation changed. */
  evidenceRecordIds: string[]
  at: number
  eventIndex: number
  /** Highest durable node ordinal present when this evidence event landed. */
  nodeOrdinalAtCheck?: number
  beforeAt?: number
  readingBefore?: SourceReading
  readingAfter: SourceReading
  /** Present once an explicit rebase authored a new head for this change. */
  rebasedAt?: number
  rebaseOpId?: string
  /** Every completed pass that covered part of this same material change. */
  rebaseOpIds?: string[]
  /** Exact stale claims the completed pass actually covered. */
  rebasedNodeIds?: string[]
}

export interface EvidenceStale {
  /** Newest durable chronology first when several changed premises arrive. */
  changes: EvidenceChange[]
}

const normal = (s: string): string => s.trim().replace(/\s+/g, ' ').toLowerCase()

const modelKey = (v: Verification): string => `${v.model.provider}:${v.model.id}`

const sourceKey = (s: WebSource): string => canonicalUrl(s.url)

const sourceLabel = (s: WebSource): string => s.title || s.url

const sourceFingerprint = (s: WebSource): string =>
  [
    s.title,
    s.quote,
    s.stance,
    s.via,
    String(s.quoteFound),
    String(s.textComplete),
    s.textHash ?? '',
    s.simHash ?? '',
  ].join('\u0000')

const corpusKey = (c: NonNullable<Verification['corpus']>['cites'][number]): string =>
  `${c.docId}#${c.passage}`

const corpusLabel = (c: NonNullable<Verification['corpus']>['cites'][number]): string =>
  `${c.docTitle} · passage ${c.passage}`

const corpusFingerprint = (c: NonNullable<Verification['corpus']>['cites'][number]): string =>
  [c.docTitle, c.quote, c.notice ?? ''].join('\u0000')

const priorKey = (p: PriorArt): string => p.ref
const priorLabel = (p: PriorArt): string => p.covers
const priorFingerprint = (p: PriorArt): string => `${p.covers}\u0000${p.closeness}`

function deltaOf<T>(
  before: readonly T[],
  after: readonly T[],
  keyOf: (row: T) => string,
  labelOf: (row: T) => string,
  fingerprintOf: (row: T) => string,
): EvidenceDelta {
  const left = new Map(before.map((row) => [keyOf(row), row]))
  const right = new Map(after.map((row) => [keyOf(row), row]))
  const added: DeltaEntry[] = []
  const removed: DeltaEntry[] = []
  const changed: DeltaEntry[] = []

  for (const [key, row] of right) {
    const old = left.get(key)
    if (!old) added.push({ key, label: labelOf(row) })
    else if (fingerprintOf(old) !== fingerprintOf(row)) {
      changed.push({ key, label: labelOf(row) })
    }
  }
  for (const [key, row] of left) {
    if (!right.has(key)) removed.push({ key, label: labelOf(row) })
  }
  return { added, removed, changed }
}

function stringDelta(before: readonly string[], after: readonly string[]): EvidenceDelta {
  return deltaOf(before, after, normal, (s) => s, normal)
}

const changed = (d: EvidenceDelta): boolean =>
  d.added.length > 0 || d.removed.length > 0 || d.changed.length > 0

interface NoticeRow {
  key: string
  label: string
}

const noticesOf = (v: Verification): NoticeRow[] =>
  (v.corpus?.cites ?? [])
    .filter((c) => c.notice)
    .map((c) => ({ key: corpusKey(c), label: c.notice! }))

/** All checks on a node, oldest first. The returned array is a new view. */
export function verificationEvents(
  node: Pick<ReasonNode, 'verification' | 'verificationHistory'>,
): Verification[] {
  return mergeVerificationEvents(
    node.verificationHistory,
    node.verification ? [node.verification] : undefined,
  )
}

const verificationIdentity = (v: Verification): string =>
  v.opId ?? `${v.checkedAt}:${v.model.provider}:${v.model.id}:${v.reading}`

const archiveIdentity = (row: VerificationArchiveEntry): string =>
  `${row.nodeId}:${verificationIdentity(row.verification)}`

/** One landing-ordered, duplicate-free verification stream from any stored views. */
export function mergeVerificationEvents(
  ...groups: Array<readonly Verification[] | undefined>
): Verification[] {
  const seen = new Set<string>()
  const rows: Verification[] = []
  for (const group of groups) {
    for (const verification of group ?? []) {
      const key = verificationIdentity(verification)
      if (seen.has(key)) continue
      seen.add(key)
      // Array position is landing order. Wall clocks can move backwards between
      // two real checks; sorting by `checkedAt` would promote an older pass to
      // current on reload even though the append-only stream already knows.
      rows.push(verification)
    }
  }
  return rows
}

/** A stable union for undo/redo and repeated prune/restore cycles. */
export function mergeVerificationArchive(
  ...groups: Array<readonly VerificationArchiveEntry[] | undefined>
): VerificationArchiveEntry[] {
  const seen = new Set<string>()
  const rows: VerificationArchiveEntry[] = []
  for (const group of groups) {
    for (const row of group ?? []) {
      const key = archiveIdentity(row)
      if (seen.has(key)) continue
      seen.add(key)
      rows.push(row)
    }
  }
  return rows
}

/** Add every check on these nodes to the session archive before removing them. */
export function archiveVerificationsFor(
  session: Session,
  nodeIds: Iterable<string>,
): VerificationArchiveEntry[] {
  const added: VerificationArchiveEntry[] = []
  for (const id of nodeIds) {
    const node = session.nodes[id]
    if (!node) continue
    for (const verification of verificationEvents(node)) {
      added.push({ nodeId: id, noveltyAtCheck: node.novelty, verification })
    }
  }
  return mergeVerificationArchive(session.verificationArchive, added)
}

/** Archived checks whose claim is not currently available as a node card. */
export function orphanedVerificationEntries(session: Session): VerificationArchiveEntry[] {
  const onGraph = new Set<string>()
  for (const id of session.order) {
    const node = session.nodes[id]
    if (!node) continue
    for (const verification of verificationEvents(node)) {
      onGraph.add(`${id}:${verificationIdentity(verification)}`)
    }
  }
  return mergeVerificationArchive(session.verificationArchive).filter(
    (row) => !onGraph.has(archiveIdentity(row)),
  )
}

/** The last check that had landed at the given instant. */
export function verificationAt(
  node: Pick<ReasonNode, 'verification' | 'verificationHistory'>,
  asOf: number,
): Verification | undefined {
  const events = verificationEvents(node)
  for (let i = events.length - 1; i >= 0; i--) {
    if (events[i]!.checkedAt <= asOf) return events[i]
  }
  return undefined
}

/**
 * Move the current check into the immutable history and install its successor.
 *
 * The caller supplies the record only after the live citation gate has admitted
 * it. This function does no epistemic adjudication; it only changes cardinality.
 */
export function appendVerification(
  node: Pick<ReasonNode, 'verification' | 'verificationHistory'>,
  next: Verification,
): Pick<ReasonNode, 'verification' | 'verificationHistory'> {
  const previous = node.verification
  return {
    verification: next,
    verificationHistory: previous
      ? [...(node.verificationHistory ?? []), previous]
      : node.verificationHistory,
  }
}

/** A mechanical before/after comparison over two admitted check records. */
export function diffVerifications(
  before: Verification | undefined,
  after: Verification,
): VerificationDiff {
  const sources = deltaOf(
    before?.sources ?? [],
    after.sources,
    sourceKey,
    sourceLabel,
    sourceFingerprint,
  )
  const corpus = deltaOf(
    before?.corpus?.cites ?? [],
    after.corpus?.cites ?? [],
    corpusKey,
    corpusLabel,
    corpusFingerprint,
  )
  const priorArt = deltaOf(
    before?.priorArt ?? [],
    after.priorArt,
    priorKey,
    priorLabel,
    priorFingerprint,
  )
  const queries = stringDelta(before?.queries ?? [], after.queries)
  const failures = stringDelta(before?.failures ?? [], after.failures ?? [])
  const notices = deltaOf(
    before ? noticesOf(before) : [],
    noticesOf(after),
    (notice) => notice.key,
    (notice) => notice.label,
    (notice) => normal(notice.label),
  )
  const readingChanged = before !== undefined && before.reading !== after.reading
  const claimChanged = Boolean(
    before &&
    (before.claimHashAtCheck && after.claimHashAtCheck
      ? before.claimHashAtCheck !== after.claimHashAtCheck
      : before.claimAtCheck &&
        after.claimAtCheck &&
        normal(before.claimAtCheck) !== normal(after.claimAtCheck)),
  )
  const modelChanged = before !== undefined && modelKey(before) !== modelKey(after)
  const scopeChanged = before !== undefined && before.scope !== after.scope
  const partialChanged = before !== undefined && Boolean(before.partial) !== Boolean(after.partial)
  const countsChanged = Boolean(
    before &&
    (before.seen !== after.seen ||
      before.dropped !== after.dropped ||
      before.corpus?.dropped !== after.corpus?.dropped ||
      before.corpus?.offered !== after.corpus?.offered ||
      before.corpus?.docsAtCheck !== after.corpus?.docsAtCheck),
  )
  const coverageChanged = Boolean(
    before &&
    (before.corpus?.match !== after.corpus?.match ||
      before.corpus?.bestCoverage !== after.corpus?.bestCoverage),
  )
  const summaryChanged = Boolean(
    before && normal(before.summary ?? '') !== normal(after.summary ?? ''),
  )

  // A reading is not a record. A documents-only check whose passages did not
  // address the claim reads nothing-found with nothing admitted (D321), and an
  // absence with no record behind it gives the reasoning below nothing to rest on.
  const emptyAbsence =
    after.reading === 'nothing-found' && !after.sources.length && !after.corpus?.cites.length
  const firstCheckReachedRecord =
    before === undefined &&
    after.reading !== 'not-checkable' &&
    after.reading !== 'unsourced' &&
    !emptyAbsence
  const typedRecordChanged =
    readingChanged ||
    claimChanged ||
    scopeChanged ||
    partialChanged ||
    countsChanged ||
    coverageChanged ||
    changed(sources) ||
    changed(corpus) ||
    changed(priorArt) ||
    changed(queries) ||
    changed(failures) ||
    changed(notices)
  // The first event has no evidence baseline, so every query would otherwise be
  // an "addition" and even a rejected/unsourced pass would detach the graph. It
  // becomes a premise only when the gate admitted an actual search record.
  const affectsReasoning = before === undefined ? firstCheckReachedRecord : typedRecordChanged
  // A model-authored summary is useful in the visible diff, but prose changing
  // cannot mechanically detach a dependency branch. Only typed fields from the
  // admitted record do that. The same distinction keeps a provider switch
  // visible without pretending a different model is itself new evidence.
  const hasChanges = typedRecordChanged || firstCheckReachedRecord || modelChanged || summaryChanged

  return {
    ...(before ? { beforeAt: before.checkedAt, readingBefore: before.reading } : {}),
    afterAt: after.checkedAt,
    readingAfter: after.reading,
    readingChanged,
    claimChanged,
    modelChanged,
    scopeChanged,
    partialChanged,
    countsChanged,
    coverageChanged,
    summaryChanged,
    sources,
    corpus,
    priorArt,
    queries,
    failures,
    notices,
    hasChanges,
    affectsReasoning,
  }
}

function evidenceFingerprints(verification: Verification | undefined): Map<string, string> {
  const rows = new Map<string, string>()
  for (const source of verification?.sources ?? []) {
    rows.set(webEvidenceRecordId(source.url), sourceFingerprint(source))
  }
  for (const cite of verification?.corpus?.cites ?? []) {
    rows.set(corpusEvidenceRecordId(cite.docId), corpusFingerprint(cite))
  }
  return rows
}

/** Exact admitted records added, removed, or changed between two checks. */
function changedEvidenceRecordIds(before: Verification | undefined, after: Verification): string[] {
  const left = evidenceFingerprints(before)
  const right = evidenceFingerprints(after)
  return [...new Set([...left.keys(), ...right.keys()])]
    .filter((id) => left.get(id) !== right.get(id))
    .sort()
}

/** The newest material check change on one still-current claim. */
export function latestEvidenceChange(node: ReasonNode): EvidenceChange | null {
  if (!verificationLive(node)) return null
  const events = verificationEvents(node)
  const current = events[events.length - 1]
  if (!current) return null
  for (let i = events.length - 1; i >= 0; i--) {
    const after = events[i]!
    const before = i > 0 ? events[i - 1] : undefined
    const diff = diffVerifications(before, after)
    if (!diff.affectsReasoning) continue
    const rebases = (node.evidenceRebases ?? [])
      // A later re-check can be mechanically identical. The material change is
      // still the older event, while an explicit rebase correctly targets the
      // newest record the user reviewed. Accept any rebase on that unchanged
      // suffix; a genuinely newer material event is visited first and therefore
      // cannot borrow an older row.
      .filter((row) => {
        const targetIndex =
          row.verificationEventIndex ??
          (row.verificationOpId
            ? events.findIndex((event) => event.opId === row.verificationOpId)
            : -1)
        if (targetIndex >= 0) return targetIndex >= i && targetIndex < events.length
        // Compatibility for the first in-memory rebuilds, which named only the
        // dated record. New rows never depend on wall-clock ordering.
        return row.verificationAt >= after.checkedAt && row.verificationAt <= current.checkedAt
      })
    const rebase = rebases.at(-1)
    return {
      sourceId: node.id,
      evidenceRecordIds: changedEvidenceRecordIds(before, after),
      at: after.checkedAt,
      eventIndex: i,
      ...(after.nodeOrdinalAtCheck !== undefined
        ? { nodeOrdinalAtCheck: after.nodeOrdinalAtCheck }
        : {}),
      ...(before ? { beforeAt: before.checkedAt, readingBefore: before.reading } : {}),
      readingAfter: after.reading,
      ...(rebase
        ? {
            rebasedAt: rebase.rebasedAt,
            rebaseOpId: rebase.opId,
            rebaseOpIds: rebases.map((row) => row.opId),
            rebasedNodeIds: [...new Set(rebases.flatMap((row) => row.affected))],
          }
        : {}),
    }
  }
  return null
}

function evidenceFamilyContext(session: Session): {
  familyByRecord: ReadonlyMap<string, string>
  claimsByFamily: ReadonlyMap<string, readonly string[]>
} {
  const records = new Map<string, EvidenceRecord>(
    (session.research?.evidenceRecords ?? []).map((record) => [record.id, record]),
  )
  const recordIdsByClaim = new Map<string, string[]>()
  for (const nodeId of session.order) {
    const node = session.nodes[nodeId]
    if (!node?.verification || !verificationLive(node)) continue
    const ids: string[] = []
    for (const source of node.verification.sources) {
      const id = webEvidenceRecordId(source.url)
      ids.push(id)
      if (!records.has(id)) {
        records.set(id, { id, origin: 'web', label: source.title, url: source.url })
      }
    }
    for (const cite of node.verification.corpus?.cites ?? []) {
      const id = corpusEvidenceRecordId(cite.docId)
      ids.push(id)
      if (!records.has(id)) {
        records.set(id, { id, origin: 'corpus', label: cite.docTitle })
      }
    }
    recordIdsByClaim.set(nodeId, ids)
  }

  const genealogy = buildEvidenceGenealogy(
    [...records.values()],
    session.research?.genealogyOverrides ?? [],
  )
  const familyByRecord = new Map(
    genealogy.families.flatMap((family) =>
      family.itemIds.map((recordId) => [recordId, family.id] as const),
    ),
  )
  const claims = new Map<string, string[]>()
  for (const [nodeId, recordIds] of recordIdsByClaim) {
    for (const recordId of recordIds) {
      const familyId = familyByRecord.get(recordId) ?? recordId
      const held = claims.get(familyId)
      if (held) {
        if (!held.includes(nodeId)) held.push(nodeId)
      } else claims.set(familyId, [nodeId])
    }
  }
  return { familyByRecord, claimsByFamily: claims }
}

function verificationPredatesChange(
  session: Session,
  node: ReasonNode,
  change: EvidenceChange,
  opOrder: ReadonlyMap<string, number>,
): boolean {
  const verification = node.verification
  if (!verification) return true
  const changeNode = session.nodes[change.sourceId]
  const events = changeNode ? verificationEvents(changeNode) : []
  const changedVerification = events[change.eventIndex]
  const checkedOrder = verification.opId ? opOrder.get(verification.opId) : undefined
  const changeOrder = changedVerification?.opId ? opOrder.get(changedVerification.opId) : undefined
  if (checkedOrder !== undefined && changeOrder !== undefined) return checkedOrder < changeOrder
  return verification.checkedAt <= change.at
}

/** Parent and synthesis dependencies, indexed in the direction reasoning flows. */
export function dependencyChildren(session: Session): Map<string, string[]> {
  const out = new Map<string, string[]>()
  for (const id of session.order) {
    const node = session.nodes[id]
    if (!node) continue
    const parents = new Set<string>([
      ...(node.parentId ? [node.parentId] : []),
      ...(node.bridgeFrom ?? []),
    ])
    parents.delete(id)
    for (const parent of parents) {
      if (!session.nodes[parent]) continue
      const list = out.get(parent)
      if (list) list.push(id)
      else out.set(parent, [id])
    }
  }
  return out
}

let staleCache:
  | {
      nodes: Record<string, ReasonNode>
      research: Session['research']
      map: ReadonlyMap<string, EvidenceStale>
    }
  | undefined

/**
 * Every claim whose dependency cone crosses a materially newer source record.
 *
 * An observed node stops propagation. Its value came from the world rather than
 * from the reasoning above it, matching the same authority boundary `taintMap`
 * uses for a refutation. A new node below an old stale premise stays stale: its
 * own timestamp cannot repair the premise it inherited. A claim rewritten after
 * the change does not predate it, as it does not predate a refutation there.
 */
export function evidenceStaleMap(session: Session): ReadonlyMap<string, EvidenceStale> {
  if (staleCache?.nodes === session.nodes && staleCache.research === session.research) {
    return staleCache.map
  }

  const dependencies = dependencyChildren(session)
  const families = evidenceFamilyContext(session)
  const opOrder = new Map(session.ops.map((op, index) => [op.id, index]))
  const found = new Map<string, EvidenceChange[]>()

  for (const sourceId of session.order) {
    const source = session.nodes[sourceId]
    if (!source) continue
    const change = latestEvidenceChange(source)
    if (!change) continue

    const directlyLinked = new Set<string>()
    for (const recordId of change.evidenceRecordIds) {
      const familyId = families.familyByRecord.get(recordId) ?? recordId
      for (const nodeId of families.claimsByFamily.get(familyId) ?? []) {
        if (nodeId !== sourceId) directlyLinked.add(nodeId)
      }
    }
    const stack = [
      ...(dependencies.get(sourceId) ?? []).map((id) => ({
        id,
        inherited: false,
        directEvidence: false,
      })),
      ...[...directlyLinked].map((id) => ({ id, inherited: false, directEvidence: true })),
    ]
    const seen = new Set<string>()
    while (stack.length) {
      const { id, inherited, directEvidence } = stack.pop()!
      const visitKey = `${id}:${inherited ? 1 : 0}:${directEvidence ? 1 : 0}`
      if (seen.has(visitKey)) continue
      seen.add(visitKey)
      const node = session.nodes[id]
      if (!node || id === sourceId) continue
      if (node.outcome) continue

      const predatesChange = directEvidence
        ? verificationPredatesChange(session, node, change, opOrder)
        : change.nodeOrdinalAtCheck !== undefined && node.createdOrdinal !== undefined
          ? node.createdOrdinal <= change.nodeOrdinalAtCheck
          : node.createdAt < change.at
      // A claim the reader rewrote after the change is her words, typed with the
      // card naming the changed record, and its subtree re-derived by the same
      // press — the taint walk's rule for a rewrite (`writtenAt`), and for the
      // same reason. A rewrite has no ordinal, so its clock is what there is.
      // Only the node's OWN dating: under a premise that is still stale it
      // inherits, as any newer node does.
      const rewrittenSince =
        !directEvidence && node.editedAt !== undefined && writtenAt(node) >= change.at
      const stale = inherited || (predatesChange && !rewrittenSince)
      if (stale) {
        const list = found.get(id)
        if (list) {
          if (!list.some((row) => row.sourceId === sourceId)) list.push(change)
        } else found.set(id, [change])
      }
      for (const child of dependencies.get(id) ?? []) {
        stack.push({ id: child, inherited: stale, directEvidence: false })
      }
    }
  }

  const out = new Map<string, EvidenceStale>()
  for (const [id, changes] of found) {
    out.set(id, {
      changes: changes.sort(
        (a, b) =>
          (b.nodeOrdinalAtCheck ?? -1) - (a.nodeOrdinalAtCheck ?? -1) ||
          b.eventIndex - a.eventIndex ||
          b.at - a.at,
      ),
    })
  }
  const map: ReadonlyMap<string, EvidenceStale> = out
  staleCache = { nodes: session.nodes, research: session.research, map }
  return map
}

export function evidenceStaleOf(session: Session, nodeId: string): EvidenceStale | null {
  return evidenceStaleMap(session).get(nodeId) ?? null
}

/** Only source-record changes that have not yet received an explicit new head. */
export function pendingEvidenceStaleOf(session: Session, nodeId: string): EvidenceStale | null {
  const stale = evidenceStaleOf(session, nodeId)
  if (!stale) return null
  const changes = stale.changes.filter(
    (change) =>
      change.rebasedAt === undefined ||
      (change.rebasedNodeIds !== undefined && !change.rebasedNodeIds.includes(nodeId)),
  )
  return changes.length ? { changes } : null
}

let priorCache:
  | { nodes: Record<string, ReasonNode>; research: Session['research']; ids: ReadonlySet<string> }
  | undefined

/**
 * Claims on the before side of a finished rethink: stale against a changed
 * record, and every change they are stale against already has a new head that
 * covered them — `pendingEvidenceStaleOf` would say nothing about them.
 *
 * The study watched a rethink land its new heads beside the old branch at equal
 * weight, two versions stacked level with nothing to say which was current. The
 * prior version stays on the board — it is the side of the comparison D65
 * exists to keep — and the canvas recedes it (`PRIOR_VERSION_INK`) instead. A
 * claim still waiting on any change is not receded: it has not been rethought.
 */
export function priorVersionIds(session: Session): ReadonlySet<string> {
  if (priorCache?.nodes === session.nodes && priorCache.research === session.research) {
    return priorCache.ids
  }
  const ids = new Set<string>()
  for (const [id, stale] of evidenceStaleMap(session)) {
    const waiting = stale.changes.some(
      (change) =>
        change.rebasedAt === undefined ||
        (change.rebasedNodeIds !== undefined && !change.rebasedNodeIds.includes(id)),
    )
    if (!waiting) ids.add(id)
  }
  priorCache = { nodes: session.nodes, research: session.research, ids }
  return ids
}

/** How much of its ink a prior-version claim keeps on the canvas — both
 *  renderers' one number. Legible, and plainly behind the heads that replaced
 *  it; the selected claim is exempt, so the side being compared can be read. */
export const PRIOR_VERSION_INK = 0.42

/** …and its caption, which keeps more of its ink than the mark does: a caption
 *  is read, and a prior version is still the side of a comparison. */
export const PRIOR_VERSION_CAPTION = 0.6

/** Claims currently affected by this one source node's latest material change. */
export function evidenceStaleForSource(session: Session, sourceId: string): ReasonNode[] {
  const map = evidenceStaleMap(session)
  return session.order
    .map((id) => session.nodes[id])
    .filter((node): node is ReasonNode =>
      Boolean(node && map.get(node.id)?.changes.some((change) => change.sourceId === sourceId)),
    )
}

/** Claims still awaiting an explicit new head for this evidence change. */
export function pendingEvidenceStaleForSource(session: Session, sourceId: string): ReasonNode[] {
  const map = evidenceStaleMap(session)
  return session.order
    .map((id) => session.nodes[id])
    .filter((node): node is ReasonNode =>
      Boolean(
        node &&
        map
          .get(node.id)
          ?.changes.some(
            (change) =>
              change.sourceId === sourceId &&
              (change.rebasedAt === undefined ||
                (change.rebasedNodeIds !== undefined && !change.rebasedNodeIds.includes(node.id))),
          ),
      ),
    )
}

export interface EvidenceRebaseBranch {
  /** The first affected node in this dependency branch. */
  root: ReasonNode
  /** Exact affected closure reachable from `root`, in board order. */
  affectedIds: string[]
  /**
   * Only an ordinary tree child of the changed claim can be safely handed to
   * the one-source rebase pass. Cross-graph and synthesis roots retain another
   * dependency and therefore stay explicit human-review work.
   */
  action: 'rederive' | 'review'
}

/**
 * The minimal affected-branch frontier for one changed source record.
 *
 * This is a queue, not a verdict. It groups the already-stale dependency
 * closure beneath its first affected nodes and refuses to pretend a
 * cross-graph synthesis can be recreated by a pass that sees only one source.
 */
export function evidenceRebaseQueue(session: Session, sourceId: string): EvidenceRebaseBranch[] {
  const affected = pendingEvidenceStaleForSource(session, sourceId)
  if (!affected.length) return []
  const affectedIds = new Set(affected.map((node) => node.id))
  const children = dependencyChildren(session)
  const order = new Map(session.order.map((id, index) => [id, index]))

  const isReviewBoundary = (node: ReasonNode): boolean =>
    node.kind === 'synthesis' || Boolean(node.bridgeFrom?.length)
  const closureFrom = (rootId: string, stopAtReviewBoundaries: boolean): string[] => {
    const closure = new Set<string>()
    const stack = [rootId]
    while (stack.length) {
      const id = stack.pop()!
      if (closure.has(id) || !affectedIds.has(id)) continue
      const node = session.nodes[id]
      if (!node) continue
      if (id !== rootId && stopAtReviewBoundaries && isReviewBoundary(node)) continue
      closure.add(id)
      for (const child of children.get(id) ?? []) stack.push(child)
    }
    return [...closure].sort((a, b) => (order.get(a) ?? 0) - (order.get(b) ?? 0))
  }

  const hasAffectedParent = (node: ReasonNode): boolean =>
    Boolean(
      (node.parentId && affectedIds.has(node.parentId)) ||
      node.bridgeFrom?.some((id) => affectedIds.has(id)),
    )
  const frontier = affected.filter((node) => !hasAffectedParent(node))
  const queued: EvidenceRebaseBranch[] = []
  const covered = new Set<string>()
  const enqueue = (root: ReasonNode, forceReview = false) => {
    const action =
      !forceReview && root.parentId === sourceId && !isReviewBoundary(root) ? 'rederive' : 'review'
    const branchIds = closureFrom(root.id, action === 'rederive')
    for (const id of branchIds) covered.add(id)
    queued.push({
      root,
      affectedIds: branchIds,
      action,
    })
  }

  for (const root of frontier) enqueue(root)
  // Malformed imported cycles have no frontier. Keep their affected work
  // visible as review-only rather than silently returning an empty queue.
  for (const node of affected) {
    if (!covered.has(node.id)) enqueue(node, true)
  }
  return queued
}

/**
 * The direct tree branches safe to rebuild from one checked node.
 *
 * Synthesis dependents are marked stale too, but deleting one from the other
 * side of the graph and pretending a single branch pass can recreate it would
 * lose the second-parent operation. They remain visible for explicit review.
 */
export function rebaseTreeRoots(session: Session, sourceId: string): ReasonNode[] {
  return evidenceRebaseQueue(session, sourceId)
    .filter((branch) => branch.action === 'rederive')
    .map((branch) => branch.root)
}
